/**
 * The one definition of what a client may upload. The drop zone's pre-check, the `prepareUpload`
 * and `finalizeUpload` server actions, and the `invoices` bucket's own limit (migration
 * `20261028100000_invoices_bucket_upload_limits.sql`) all derive from these values.
 */
export const MAX_UPLOAD_MB = 25
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
}

const ACCEPTED_UPLOAD_MIME_TYPES: readonly string[] = [...new Set(Object.values(MIME_BY_EXTENSION))]

/** The `accept` attribute value for a file input. */
export const UPLOAD_ACCEPT_ATTRIBUTE = Object.keys(MIME_BY_EXTENSION)
  .map((ext) => `.${ext}`)
  .join(",")

const GENERIC_BINARY_TYPE = "application/octet-stream"

/**
 * The accepted MIME type for a file, or null when its type is not accepted. A browser reports an
 * empty or generic `application/octet-stream` type for some files, so the extension is the
 * fallback in those two cases.
 */
export function resolveUploadMimeType(fileName: string, reportedType: string): string | null {
  if (reportedType && reportedType !== GENERIC_BINARY_TYPE) {
    return ACCEPTED_UPLOAD_MIME_TYPES.includes(reportedType) ? reportedType : null
  }
  const extension = fileName.split(".").pop()?.toLowerCase() ?? ""
  return MIME_BY_EXTENSION[extension] ?? null
}

/** Why a file the browser holds is not accepted; the drop zone words each from the catalog. */
export type UploadRefusal = { reason: "empty" | "tooLarge" | "type"; name: string; size: number }

/**
 * Every reason an upload can be refused, as a stable code. The server actions return one of these
 * (never a sentence) and the browser words it from the catalog
 * (`documents.uploadFailure.reason.<code>`), so a refusal reads in the user's language.
 */
export type UploadRefusalCode = UploadRefusal["reason"] | "sessionExpired" | "payerRequired" | "notOwned" | "noPermission"

/** Why a file may not be uploaded, or null when it may. */
export function uploadRefusal(file: { name: string; size: number; type: string }): UploadRefusal | null {
  if (file.size <= 0) return { reason: "empty", name: file.name, size: file.size }
  if (file.size > MAX_UPLOAD_BYTES) return { reason: "tooLarge", name: file.name, size: file.size }
  if (!resolveUploadMimeType(file.name, file.type)) return { reason: "type", name: file.name, size: file.size }
  return null
}
